"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseAsset = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const Compliance_1 = require("./Compliance");
const Documents_1 = require("./Documents");
const Metadata_1 = require("./Metadata");
const Permissions_1 = require("./Permissions");
const internal_1 = require("../../../../internal");
const types_1 = require("../../../../types");
const utils_1 = require("../../../../utils");
const conversion_1 = require("../../../../utils/conversion");
const internal_2 = require("../../../../utils/internal");
/**
 * Class used to manage functionality common to all assets.
 *
 */
class BaseAsset extends internal_1.Entity {
    /**
     * @hidden
     *
     * @note It is generally preferable to instantiate `FungibleAsset` or `NftCollection`
     */
    constructor(identifiers, context) {
        super(identifiers, context);
        const { assetId } = identifiers;
        this.id = assetId;
        this.compliance = new Compliance_1.Compliance(this, context);
        this.documents = new Documents_1.Documents(this, context);
        this.metadata = new Metadata_1.Metadata(this, context);
        this.permissions = new Permissions_1.Permissions(this, context);
        this.freeze = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: () => [internal_1.toggleFreezeTransfers, { asset: this, freeze: true }],
            voidArgs: true,
        }, context);
        this.unfreeze = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: () => [internal_1.toggleFreezeTransfers, { asset: this, freeze: false }],
            voidArgs: true,
        }, context);
        this.transferOwnership = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: args => [internal_1.transferAssetOwnership, Object.assign({ asset: this }, args)] }, context);
        this.addRequiredMediators = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.addAssetMediators, Object.assign({ asset: this }, args)],
        }, context);
        this.removeRequiredMediators = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.removeAssetMediators, Object.assign({ asset: this }, args)],
        }, context);
        this.setVenueFiltering = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: args => [internal_1.setVenueFiltering, Object.assign({ asset: this }, args)] }, context);
        this.modify = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: args => [internal_1.modifyAsset, Object.assign({ asset: this }, args)] }, context);
        this.linkTicker = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.linkTickerToAsset, Object.assign({ asset: this }, args)],
        }, context);
        this.unlinkTicker = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: () => [internal_1.unlinkTickerFromAsset, { asset: this }],
            voidArgs: true,
        }, context);
    }
    /**
     * Unique ID of the Asset in hex format
     *
     * @note Although UUID format is the usual representation of asset IDs, generic polkadot/substrate tools usually expect it in hex format
     */
    get rawId() {
        return (0, utils_1.uuidToHex)(this.id);
    }
    /**
     * @hidden
     * Check if a value is of type {@link UniqueIdentifiers}
     */
    static isUniqueIdentifiers(identifier) {
        const { assetId } = identifier;
        return typeof assetId === 'string';
    }
    // eslint-disable-next-line require-jsdoc
    getIdentifiers(callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset }, }, }, context, } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(this, context);
            if (callback) {
                context.assertSupportsSubscription();
                return asset.assetIdentifiers(rawAssetId, (identifiers) => {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- callback errors should be handled by the caller
                    callback(identifiers.map(conversion_1.assetIdentifierToSecurityIdentifier));
                });
            }
            const assetIdentifiers = yield asset.assetIdentifiers(rawAssetId);
            return assetIdentifiers.map(conversion_1.assetIdentifierToSecurityIdentifier);
        });
    }
    // eslint-disable-next-line require-jsdoc
    isFrozen(callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset }, }, }, context, } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(this, context);
            if (callback) {
                context.assertSupportsSubscription();
                return asset.frozen(rawAssetId, frozen => {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- callback errors should be handled by the caller
                    callback((0, conversion_1.boolToBoolean)(frozen));
                });
            }
            const result = yield asset.frozen(rawAssetId);
            return (0, conversion_1.boolToBoolean)(result);
        });
    }
    // eslint-disable-next-line require-jsdoc
    details(callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset, externalAgents }, }, }, context, } = this;
            const assembleResult = (optToken, agentGroups, assetName, tickerValue) => __awaiter(this, void 0, void 0, function* () {
                const fullAgents = [];
                if (optToken.isNone) {
                    throw new internal_1.PolymeshError({
                        message: 'Asset detail information not found',
                        code: types_1.ErrorCode.DataUnavailable,
                    });
                }
                const { totalSupply, divisible, ownerDid, assetType: rawAssetType } = optToken.unwrap();
                agentGroups.forEach(([storageKey, agentGroup]) => {
                    const rawAgentGroup = agentGroup.unwrap();
                    if (rawAgentGroup.isFull) {
                        fullAgents.push(new internal_1.Identity({ did: (0, conversion_1.identityIdToString)(storageKey.args[1]) }, context));
                    }
                });
                const owner = new internal_1.Identity({ did: (0, conversion_1.identityIdToString)(ownerDid) }, context);
                const { value, type } = (0, conversion_1.assetTypeToKnownOrId)(rawAssetType);
                let assetType;
                if (value instanceof bignumber_js_1.default) {
                    const customType = yield asset.customTypes((0, conversion_1.bigNumberToU32)(value, context));
                    assetType = (0, conversion_1.bytesToString)(customType);
                }
                else {
                    assetType = value;
                }
                let ticker;
                if (tickerValue.isSome) {
                    ticker = (0, conversion_1.tickerToString)(tickerValue.unwrap());
                }
                return {
                    assetType,
                    nonFungible: type === 'NonFungible',
                    isDivisible: (0, conversion_1.boolToBoolean)(divisible),
                    name: (0, conversion_1.bytesToString)(assetName.unwrap()),
                    owner,
                    totalSupply: (0, conversion_1.balanceToBigNumber)(totalSupply),
                    fullAgents,
                    ticker,
                };
            });
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(this, context);
            const groupOfAgentPromise = externalAgents.groupOfAgent.entries(rawAssetId);
            const namePromise = asset.assetNames(rawAssetId);
            const tickerPromise = asset.assetIdTicker(rawAssetId);
            if (callback) {
                context.assertSupportsSubscription();
                const groupEntries = yield groupOfAgentPromise;
                const assetName = yield namePromise;
                const tickerValue = yield tickerPromise;
                return asset.assets(rawAssetId, (securityToken) => __awaiter(this, void 0, void 0, function* () {
                    const result = yield assembleResult(securityToken, groupEntries, assetName, tickerValue);
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- callback errors should be handled by the caller
                    callback(result);
                }));
            }
            const [token, groups, name, tickerValue] = yield Promise.all([
                asset.assets(rawAssetId),
                groupOfAgentPromise,
                namePromise,
                tickerPromise,
            ]);
            return assembleResult(token, groups, name, tickerValue);
        });
    }
    /**
     * Get required Asset mediators. These Identities must approve any Instruction involving the asset
     */
    getRequiredMediators() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context, context: { polymeshApi: { query: { asset }, }, }, } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(this, context);
            const rawMediators = yield asset.mandatoryMediators(rawAssetId);
            return (0, conversion_1.identitiesSetToIdentities)(rawMediators, context);
        });
    }
    /**
     * Get venue filtering details
     */
    getVenueFilteringDetails() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context, context: { polymeshApi: { query }, }, } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(this, context);
            const [rawIsEnabled, venueEntries] = yield Promise.all([
                query.settlement.venueFiltering(rawAssetId),
                query.settlement.venueAllowList.entries(rawAssetId),
            ]);
            const allowedVenues = venueEntries.map(([key]) => {
                const rawId = key.args[1];
                const venueId = (0, conversion_1.u64ToBigNumber)(rawId);
                return new internal_1.Venue({ id: venueId }, context);
            });
            const isEnabled = (0, conversion_1.boolToBoolean)(rawIsEnabled);
            return {
                isEnabled,
                allowedVenues,
            };
        });
    }
    // eslint-disable-next-line require-jsdoc
    currentFundingRound(callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset }, }, }, context, } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(this, context);
            const assembleResult = (roundName) => (0, conversion_1.bytesToString)(roundName) || null;
            if (callback) {
                context.assertSupportsSubscription();
                return asset.fundingRound(rawAssetId, round => {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- callback errors should be handled by the caller
                    callback(assembleResult(round));
                });
            }
            const fundingRound = yield asset.fundingRound(rawAssetId);
            return assembleResult(fundingRound);
        });
    }
    /**
     * @hidden
     */
    exists() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context, context: { polymeshApi: { query: { asset, nft }, }, }, } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(this, context);
            const [tokenSize, nftId] = yield Promise.all([
                asset.assets.size(rawAssetId),
                nft.collectionAsset(rawAssetId),
            ]);
            return !tokenSize.isZero() && nftId.isZero();
        });
    }
    /**
     * Return the BaseAsset's ID
     */
    toHuman() {
        return this.id;
    }
}
exports.BaseAsset = BaseAsset;
//# sourceMappingURL=BaseAsset.js.map